/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { MessageOutlined } from '@ant-design/icons';
import {
  useHistory,
  Route,
  Switch,
  useRouteMatch,
  useParams,
} from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { usePrintWithRef } from '~src/hooks/usePrint';
import { useIntl } from 'react-intl';
import {
  Button,
  Modal,
  Popover,
  Tabs,
  Tooltip,
  Typography,
  message,
} from 'antd';

// local imports
import { useStyles } from '~src/hooks/useStyles';

import IconButton from '~components/_common/IconButton/IconButton';
import { FileTableProps } from '../../../atoms/FileTables/FileTable';

import { MyTendersDocumentsTable } from '../../../atoms/FileTables/MyTendersDocumentsTable';
import TenderDetails from '../../DetailsLayouts/TenderDetails';
import { useShares } from '~src/hooks/api-hooks/useShares';
import { styleFn } from './MyTendersTabs.style';
import { CreateTenderQuestionForm } from './Questions/CreateTenderQuestionForm';
import QuestionsBlock from './Questions/QuestionsBlock/QuestionsBlock';
import QuestionsPageLayoutBlock from './Questions/QuestionsBlock/QuestionsPageLayoutBlock';
import { useSharedTenderDetails } from '~src/hooks/api-hooks/useTenderDetails';
import RightSideContentWrapper from '~components/atoms/Wrappers/RightSideContentWrapper/RightSideContentWrapper';
import { CreateQuestionModal } from './Questions/QuestionsBlock/CreateQuestionModal';
import {
  loadSharedTenderAttachment,
  loadSharedTenderVersionedFile,
  rejectShareKey,
  UpdateSharedTenderState,
  updateSharedTenderState,
} from '~src/services/api/shares';
import { queryCache, useMutation } from 'react-query';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import ContractAndOffer from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractAndOffer';
import { TenderSharesBlock } from '~components/molecules/Tabs/TenderTabs/components/Shares/TenderSharesBlock';
import MovementsBlock from '~components/molecules/Tabs/TenderTabs/components/Movements/MovementsBlock';
import { useIsPath } from '~src/hooks/useIsPath';
import TenderSharesDocumentExpandable from '~components/molecules/RightSideView/components/TenderDocumentsPrint/TenderSharesDocumentExpandable';
import { RightClickOverlay } from '~components/atoms/RightClickOverlay/RightClickOverlay';
import dataContextMenu from '~src/data/dataContextMenu';
import { DbAttachment, DbFile, DbFolder, DbTenderFolder } from '~src/types/db';
import { useTenderParamsFromUrl } from '~src/hooks/useTenderParamsFromUrl';
import {
  FileViewerMargins,
  useFileViewerContext,
} from '~src/contexts/FileToViewContext';
import { saveFromLink } from '~src/utils/downloader';
import { appLog } from '~src/utils/log';
import RejectShareKeyModal from '~components/popups-modals/CachedSharesModals/RejectShareKeyModal/RejectShareKey.modal';
import { downloadTenderShareFolder } from '~src/utils/downloader/tenderShareFolderDownload';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { downloadVendorTenderFolderFile } from '~src/utils/download';
import { isTenderClosed } from '~src/utils/isTenderClosed';
import { DB_STATE } from '~src/types/enums';
import { useDynamicHeightAndLockScroll } from '~src/hooks/useDynamicHeightAndLockScroll';

const { TabPane } = Tabs;

type MyTendersTableUrlParams = {
  shareIdKey: string;
  rootFolderId?: string;
  currentFolderId?: string;
};

export const MyTendersTabs: React.FC<
  FileTableProps & {
    resetSelectedForDetails: () => void;
    bottomViewHeight?: number;
  }
> = ({
  selectedKeys,
  highlightedRow,
  onSelect,
  onSelectAll,
  setSelectedItems,
  handleRow,
  resetSelectedForDetails,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const { path, url } = useRouteMatch();
  const intl = useIntl();
  const history = useHistory();

  const [isCommentPopoverVisible, setIsCommentPopoverVisible] = useState(false);
  const [isPrintModalVisible, setIsPrintModalVisible] = useState(false);

  const [activeTab, setActiveTab] = useState<string | undefined>();

  const [visibleCreateQuestionModal, setVisibleCreateQuestionModal] = useState(
    false
  );
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  // Primary-/Secondary Share Keys (Subcontractors tab): the Invite trigger
  // now lives here in the page header (tabBarExtraContent left slot),
  // mirroring the admin's own Invite button living outside its table in
  // TenderActions.tsx - TenderSharesBlock/TenderSharesListTable just render
  // the Modal, controlled by this state.
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const [isDocumentPrintExpanded, setIsDocumentPrintExpanded] = useState(false);
  const [isContractPrintExpanded, setIsContractPrintExpanded] = useState(false);
  const [isMovementsPrintExpanded, setIsMovementsPrintExpanded] = useState(
    false
  );
  const [isQuestionsPrintExpanded, setIsQuestionsPrintExpanded] = useState(
    false
  );

  const [rightClickEntity, setRightClickEntity] = useState<{
    attachment?: DbAttachment;
    tenderFolder?: DbTenderFolder;
    noFiles?: boolean;
  }>({});

  const [allFoldersForDownload, setAllFoldersForDownload] = useState<
    DbTenderFolder[]
  >([]);

  const { currentFolderId, rootFolderId } = useTenderParamsFromUrl({
    isOnShare: true,
  });
  const isFirstLevelDocs = !currentFolderId;

  const { setFileToView } = useFileViewerContext();
  const { fileVersionsContext } = useContext(FileVersionsContext);

  const handleSelectedFileContext = (record?: any) => {
    if (!record) {
      return setRightClickEntity({ noFiles: true });
    }
    if (record.dbAttachment) {
      setRightClickEntity({ attachment: record.dbAttachment });
    } else if (record.dbTenderFolder) {
      setRightClickEntity({ tenderFolder: record.dbTenderFolder });
    } else {
      setRightClickEntity({ noFiles: true });
    }
  };

  const getRightClickItems = () => {
    const ALLOW = new Set(['file-download', 'folder-download']);

    if (rightClickEntity.attachment) {
      return (dataContextMenu.attachment || []).filter((i: any) =>
        ALLOW.has(i.modal)
      );
    }

    if (rightClickEntity.tenderFolder) {
      const folderMenu =
        (dataContextMenu as any).tenderFolder ||
        (dataContextMenu as any).folder ||
        (dataContextMenu as any).tender_folder ||
        [];
      return (folderMenu || []).filter((i: any) => ALLOW.has(i.modal));
    }

    return [];
  };

  // =========================
  // URL params (NEW: /my-tenders/:shareIdKey)
  // shareIdKey = `${shareId}-${shareKeyId}`
  // =========================
  const { shareIdKey } = useParams<MyTendersTableUrlParams>();

  const { shareId, shareKeyFromUrl } = useMemo(() => {
    const raw = String(shareIdKey || '');
    const parts = raw.split('-');

    const id = parts[0] || '';
    const key = parts.slice(1).join('-') || '';

    return {
      shareId: id,
      shareKeyFromUrl: key,
    };
  }, [shareIdKey]);

  // Keep variable names used in file to minimize changes:
  const tenderId = shareId;
  const shareKey = shareKeyFromUrl;

  const rightClickAction = async (modal: string) => {
    // preview (svf/pdf/img etc)
    if (
      modal === 'svf-view-file' ||
      modal === 'window-view-file' ||
      modal === 'fullscreen-view-file'
    ) {
      const att = rightClickEntity.attachment;
      if (!att) return;

      if (!tenderId || !shareKey) {
        appLog('[MyTendersTabs] missing shareId/shareKey for preview', {
          tenderId,
          shareKey,
          shareIdKey,
        });
        return;
      }

      if (isFirstLevelDocs) {
        const res = await loadSharedTenderAttachment({
          file_guid: att.id,
          share_id: tenderId,
          share_key: shareKey,
        });
        setFileToView(res, { marginTop: FileViewerMargins.TABS });
        return;
      } else {
        const last = att.files?.[att.files.length - 1];
        if (!last) return;
        const res = await loadSharedTenderVersionedFile({
          file_guid: String(last.file_id),
          share_id: tenderId,
          share_key: shareKey,
          folder_id: currentFolderId,
          root_folder_id: rootFolderId,
          versioned_file_id: att.id,
        });
        setFileToView(res, { marginTop: FileViewerMargins.TABS });
        return;
      }
    }

    // file download
    if (modal === 'file-download') {
      const att = rightClickEntity.attachment as any;
      if (!att) return;

      if (!tenderId || !shareKey) {
        appLog('[MyTendersTabs] missing shareId/shareKey for download', {
          tenderId,
          shareKey,
          shareIdKey,
        });
        return;
      }

      if (isFirstLevelDocs) {
        // ROOT FOLDER
        const res = await loadSharedTenderAttachment({
          file_guid: att.id,
          share_id: tenderId,
          share_key: shareKey,
        });
        res?.location && saveFromLink(res.location);
        return;
      } else {
        // VENDOR ROOT SUBFOLDERs:
        const versionId = fileVersionsContext[att.id]?.id;
        const stableVersions = (att.files || [])
          .map((f: any) => ({
            ...f,
            revision: f.serial_number ?? f.version ?? f.revision,
            version: f.serial_number ?? f.version ?? f.revision,
            id: f.registration_instance_id || f.file_id || f.id,
          }))
          .sort((a: any, b: any) => (b.revision || 0) - (a.revision || 0));

        const selectedVersion =
          stableVersions.find((v: any) => String(v.id) === String(versionId)) ||
          stableVersions[0] ||
          att.versions?.find((v: any) => String(v.id) === String(versionId)) ||
          att.versions?.[0];

        if (!selectedVersion) {
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
          return;
        }

        const registrationId =
          selectedVersion.registration_id ||
          selectedVersion.versioned_file_id ||
          att.id;
        const serialNumber =
          selectedVersion.version || selectedVersion.serial_number;

        await downloadVendorTenderFolderFile({
          shareId: tenderId,
          shareKey,
          rootFolderId: String(rootFolderId),
          folderId: String(currentFolderId),
          registrationId: String(registrationId),
          serialNumber,
          onDownloadStarted: () =>
            message.info(intl.formatMessage({ id: 'download-starting' })),
        }).catch((err) => {
          appLog('downloadSingleSharedTenderFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
        return;
      }
    }

    // folder download (Vendor-side, via share_id + share_key)
    if (modal === 'folder-download') {
      const folder = rightClickEntity.tenderFolder;
      if (!folder || !shareKey) return;

      await downloadTenderShareFolder({
        // @ts-ignore
        folder,
        allFolders: (allFoldersForDownload || []) as any,
        shareId: tenderId,
        shareKey,
        fileVersionsContext: {},
        onDownloadStarted: () => {
          //
        },
      }).catch((err) => {
        appLog('downloadSharedTenderFolder -> err', err);
        message.error(intl.formatMessage({ id: 'file-download-failed' }));
      });
      return;
    }
  };

  const selectedForOverlay: {
    file?: DbFile;
    folder?: DbFolder;
    moduleFile?: any;
  } = (() => {
    if (rightClickEntity.attachment) {
      return { file: (rightClickEntity.attachment as unknown) as DbFile };
    }
    if (rightClickEntity.tenderFolder) {
      return { folder: (rightClickEntity.tenderFolder as unknown) as DbFolder };
    }
    return {};
  })();

  const handleRowWithRC: Required<FileTableProps>['onRow'] = (
    record,
    index
  ) => {
    const base = handleRow ? handleRow(record, index) : {};
    const isFolder = !!record?.dbTenderFolder;

    return {
      ...base,
      onContextMenu: () => {
        handleSelectedFileContext(record);
      },
      onClick: base?.onClick,
    };
  };

  const {
    handlePrint: handleDocumentsToPrint,
    printRef: documentsPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => setIsDocumentPrintExpanded(true),
    onAfterPrint: () => setIsDocumentPrintExpanded(false),
  });

  const {
    handlePrint: handleContractToPrint,
    printRef: contractPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => setIsContractPrintExpanded(true),
    onAfterPrint: () => setIsContractPrintExpanded(false),
  });

  const {
    handlePrint: handleMovementsToPrint,
    printRef: movementsPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => setIsMovementsPrintExpanded(true),
    onAfterPrint: () => setIsMovementsPrintExpanded(false),
  });

  const {
    handlePrint: handleQuestionsToPrint,
    printRef: questionsPrintRef,
  } = usePrintWithRef({
    onBeforeGetContent: () => setIsQuestionsPrintExpanded(true),
    onAfterPrint: () => setIsQuestionsPrintExpanded(false),
  });

  const isOnTender = useIsPath('/tenders', { endsWith: true });
  const isOnShares = useIsPath('/shares', { endsWith: true });
  const isOnQA = useIsPath('/questions', { endsWith: true });
  const isOnMovements = useIsPath('/movements', { endsWith: true });

  // keep this query (can be used elsewhere in future), but shareKey is now derived from URL
  const shareTendersQuery = useShares({}, { refetchOnMount: false });

  const shareTenderDetailsQuery = useSharedTenderDetails(
    { id: tenderId, shareKey },
    { enabled: !!tenderId && !!shareKey }
  );

  // Primary-/Secondary Share Keys: the "Subcontractors" tab is only for a
  // Primary-Share-Key, and only once the admin has turned the feature on
  // for this tender. `share_key_is_primary` lives on /my_shares's `key`
  // object (this query), not on the separate tender_shares/{id}/registration
  // response `shareTenderDetailsQuery` uses - default to true when absent,
  // same "guess" convention used elsewhere for this not-yet-fully-confirmed
  // field.
  // Match on both share_id AND key.share_key_id (shareKey comes from the
  // URL, parsed above) - matching share_id alone would collapse two
  // entries that share the same tender (one Primary key, one Secondary
  // key) into whichever happens to come first in shareTendersQuery.data,
  // regardless of which of the two the vendor actually navigated into.
  // Fallback to share_id-only when shareKey is empty (old /my-tenders/:id
  // links, see HomeLeftSideView.tsx's "Backward compatibility" comment).
  const currentShareListItem = shareTendersQuery.data?.find(
    (item) =>
      item.share_id === tenderId &&
      (!shareKey || item.key?.share_key_id === shareKey)
  );
  const isPrimaryShareKey =
    currentShareListItem?.key?.share_key_is_primary !== false;

  // Rejected tenders (see HomeLeftSideView.tsx / ProjectWebMyTendersTable.tsx
  // for the same share_key_state check) can still be opened by direct link
  // or via "show rejected" - hide the Reject action once already rejected.
  const isShareKeyRejected =
    currentShareListItem?.key?.share_key_state === DB_STATE.REJECTED;
  const secondaryKeysOn = Boolean(
    shareTenderDetailsQuery.data?.share_is_secondary_key_on
  );
  const showSubcontractorsTab = secondaryKeysOn && isPrimaryShareKey;

  // Primary-/Secondary Share Keys: blocks inviting new subcontractors once
  // the tender is closed, same rule as the primary Vendors "Invite" flow -
  // uses the same isTenderClosed helper ContractAndOffer.tsx already uses.
  // Also re-derived (independently) inside TenderSharesBlock.tsx for the
  // table's own disabled logic - both read the exact same query data
  // through the same pure helper, so there's no correctness risk, just a
  // small accepted duplication (this button lives outside that tree).
  const subcontractorsTenderCompleted = isTenderClosed(
    shareTenderDetailsQuery.data
  );

  // Placed in styles.actionsContainer (top-right, same row as Reject) - to
  // Reject's LEFT, not in the tab bar's own left slot - only while the
  // Subcontractors tab is active. Reuses the same buttonDiv/IconButton/p
  // structure Reject already uses, so it lines up visually.
  const inviteButtonNode = (
    <Tooltip
      title={
        subcontractorsTenderCompleted &&
        intl.formatMessage({ id: 'tender-data-completed' })
      }
      placement="bottom">
      <div
        css={styles.buttonDiv}
        onClick={() => {
          if (subcontractorsTenderCompleted) return;
          setIsInviteModalOpen(true);
        }}
        style={
          subcontractorsTenderCompleted
            ? { opacity: 0.5, cursor: 'not-allowed' }
            : undefined
        }>
        <IconButton
          iconStyle="far"
          icon="paper-plane"
          customStyle={styles.iconsHeader}
          backgroundOff
          disabled={subcontractorsTenderCompleted}
        />
        <p css={styles.actionsRightButton}>
          {intl.formatMessage({ id: 'invite' })}
        </p>
      </div>
    </Tooltip>
  );

  appLog(
    `MyTenderTabs v1`,
    shareKey,
    shareTendersQuery.data,
    shareTenderDetailsQuery.data
  );

  const [
    dbRejectTenderShare,
    { isLoading: isUpdateTenderStateLoading },
  ] = useMutation(
    (
      args: Omit<UpdateSharedTenderState, 'action'> & {
        message?: string;
        deleteAfter?: boolean;
      }
    ) =>
      rejectShareKey({
        shareId: args.shareId,
        shareKey: args.shareKey,
        message: args.message,
      }),
    {
      onSuccess: () => {
        queryCache.invalidateQueries('shares');
        queryCache.invalidateQueries([
          QueryKeys.sharedTenderDetails,
          { id: tenderId, shareKey },
        ]);

        history.push('/my-tenders');
      },
      onError: (err) => {
        message.error(intl.formatMessage({ id: 'tender-share-update-error' }));
      },
      onSettled: () => {
        setRejectModalVisible(false);
      },
    }
  );

  const handleRejectKey = (text: string, deleteAfter: boolean) => {
    if (!tenderId || !shareKey) return;

    dbRejectTenderShare({
      shareId: tenderId,
      shareKey: shareKey,
      message: text.trim() || undefined,
    });
  };

  const basePath = useMemo(() => path.split('/').slice(0, 3).join('/'), [path]);
  const baseUrl = useMemo(() => url.split('/').slice(0, 3).join('/'), [url]);

  // IMPORTANT:
  // Documents must render both:
  // - /my-tenders/:shareIdKey/documents
  // - /my-tenders/:shareIdKey/tender-folders/:rootFolderId/folders/:currentFolderId/documents
  const documentsRoutes = useMemo(
    () => [
      `${basePath}/documents`,
      `${basePath}/tender-folders/:rootFolderId/folders/:currentFolderId/documents`,
    ],
    [basePath]
  );

  const handleTabChange = (key: string) => {
    resetSelectedForDetails();
    setActiveTab(key);

    // If we're already inside documents folder navigation, do NOT replace url to /documents.
    // Otherwise we lose folder context and some hooks may parse wrong route.
    const isInTenderFolders = history.location.pathname.includes(
      '/tender-folders/'
    );
    if (key === 'documents' && isInTenderFolders) {
      return;
    }

    return history.replace(`${baseUrl}/${key}`);
  };

  // Handle default selected tab key
  useEffect(() => {
    const pathSegments = history.location.pathname.split('/').filter(Boolean);

    const directMapping = {
      details: 'details',
      documents: 'documents',
      questions: 'questions',
      contract: 'contract',
      help: 'help',
      movements: 'movements',
      subcontractors: 'subcontractors',
    };

    setActiveTab((prevActiveTab) => {
      const currentActiveTab = pathSegments.find((segment) => {
        return Object.values(directMapping).includes(segment);
      });

      // Primary-/Secondary Share Keys: a Secondary-Share-Key vendor may only
      // access "tender notice" (Details) + Documents - Contract/Questions/
      // Subcontractors all 401 server-side for it. Redirect any deep-link
      // into one of those away to Details, mirroring the backend rule
      // instead of just hiding the tab buttons below.
      if (
        !isPrimaryShareKey &&
        currentActiveTab &&
        ['contract', 'questions', 'subcontractors'].includes(currentActiveTab)
      ) {
        handleTabChange(directMapping.details);
        return;
      }

      if (!currentActiveTab) {
        // If we are inside folders route, we still want Documents tab active,
        // but we must NOT redirect to /documents (keep current folder route).
        if (pathSegments.includes('tender-folders')) {
          return directMapping.documents;
        }
        // Default
        handleTabChange(directMapping.details);
        return;
      }

      return currentActiveTab || prevActiveTab;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname, isPrimaryShareKey]);

  return (
    <div
      css={styles.container}
      style={{
        marginTop: '-1.5px',
        display: 'flex',
        justifyContent: 'space-between',
        // backgroundColor: 'blue',
      }}>
      <Tabs
        activeKey={activeTab}
        style={{ marginTop: '-2px', width: '100%' }}
        tabBarStyle={{
          // maxWidth: 'calc(100% - 230px)',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
        onChange={handleTabChange}>
        <TabPane
          tab={
            <div style={{ paddingLeft: '8px' }}>
              {intl.formatMessage({ id: 'tab-details' })}
            </div>
          }
          key="details">
          <Switch>
            <Route path={`${basePath}/details`}>
              <RightSideContentWrapper>
                <TenderDetails
                  tenderData={shareTenderDetailsQuery.data}
                  tenderId={
                    shareTenderDetailsQuery.data
                      ? String(shareTenderDetailsQuery.data.id)
                      : ''
                  }
                  isShares={
                    shareKey ? { shareId: tenderId, shareKey } : undefined
                  }
                  secondaryKeyInfo={
                    !isPrimaryShareKey
                      ? {
                          parentInviter:
                            currentShareListItem?.key?.parent_inviter,
                        }
                      : undefined
                  }
                />
              </RightSideContentWrapper>
            </Route>
          </Switch>
        </TabPane>
        <TabPane
          tab={
            <span
              onClick={() => {
                history.push(
                  `/my-tenders/${tenderId}/share/${shareKey}/documents`
                );
              }}>
              {intl.formatMessage({ id: 'tab-documents' })}
            </span>
          }
          key="documents">
          <div css={{ display: 'none' }}>
            <TenderSharesDocumentExpandable
              tenderId={tenderId}
              shareId={tenderId}
              shareKey={shareKey}
              ref={documentsPrintRef}
              isStartFetch={isDocumentPrintExpanded}
            />
          </div>
          <Switch>
            <Route path={documentsRoutes}>
              <RightClickOverlay
                items={getRightClickItems()}
                onItemClick={(item) => rightClickAction(item.modal)}
                onOverlayClose={() => setRightClickEntity({})}
                selected={selectedForOverlay}>
                <MyTendersDocumentsTable
                  onFoldersListChange={(folders) =>
                    setAllFoldersForDownload((folders || []) as any)
                  }
                  {...tableProps}
                  highlightedRow={highlightedRow}
                  handleRow={handleRowWithRC}
                  onSelect={onSelect}
                  onSelectAll={onSelectAll}
                  selectedKeys={selectedKeys}
                  setSelectedItems={setSelectedItems}
                  shareKey={shareKey}
                  shareId={tenderId}
                  onEmptyOverlay={() => setRightClickEntity({})}
                  tableColumnsConfig={{
                    onGetTenderAttachment: async ({ attachmentId }) =>
                      loadSharedTenderAttachment({
                        file_guid: attachmentId,
                        share_id: tenderId,
                        share_key: shareKey,
                      }),
                    onGetTenderVersionedFile: async ({
                      attachmentId,
                      currentFolderId,
                      rootFolderId,
                      versionId,
                    }) =>
                      loadSharedTenderVersionedFile({
                        file_guid: attachmentId,
                        share_id: tenderId,
                        share_key: shareKey,
                        folder_id: currentFolderId,
                        root_folder_id: rootFolderId,
                        versioned_file_id: versionId,
                      }),
                  }}
                />
              </RightClickOverlay>
            </Route>
          </Switch>
        </TabPane>
        {/* Primary-/Secondary Share Keys: a Secondary-Share-Key vendor has no
            access to contract/offer data (401 server-side) - only render
            this tab for a Primary-Share-Key. */}
        {isPrimaryShareKey && (
          <TabPane
            tab={intl.formatMessage({ id: 'tab-contract-name' })}
            key="contract">
            <Route exact path={`${basePath}/contract`}>
              <ContractAndOffer
                shareId={tenderId}
                shareKey={shareKey}
                ref={contractPrintRef}
                isStartFetch={isContractPrintExpanded}
                tenderRegData={shareTenderDetailsQuery.data}
              />
            </Route>
          </TabPane>
        )}
        {/* Primary-/Secondary Share Keys: List + Invite for this pass -
            Remove/Reactivate deferred to a later pass (see plan). Only
            shown to a Primary-Share-Key once the admin has turned the
            feature on for this tender. */}
        {showSubcontractorsTab && (
          <TabPane
            tab={intl.formatMessage({ id: 'tab-subcontractors' })}
            key="subcontractors">
            <Route exact path={`${basePath}/subcontractors`}>
              <SubcontractorsTabContent
                tenderId={tenderId}
                shareKey={shareKey}
                tenderRegData={shareTenderDetailsQuery.data}
                isInviteModalOpen={isInviteModalOpen}
                onCloseInviteModal={() => setIsInviteModalOpen(false)}
                selectedKeys={selectedKeys}
                onSelect={onSelect}
                onSelectAll={onSelectAll}
                setSelectedItems={setSelectedItems}
                bottomViewHeight={tableProps.bottomViewHeight}
              />
            </Route>
          </TabPane>
        )}
        {/* HIDDEN BY JIRA: TV1-490 */}
        {/*<TabPane*/}
        {/*  tab={intl.formatMessage({ id: 'tab-movements' })}*/}
        {/*  key="movements">*/}
        {/*  <Route exact path={`${basePath}/movements`}>*/}
        {/*    <div css={{ padding: '0px 10px' }}>*/}
        {/*      <MovementsBlock*/}
        {/*        shareId={tenderId}*/}
        {/*        shareKey={shareKey}*/}
        {/*        ref={movementsPrintRef}*/}
        {/*        isStartFetch={isMovementsPrintExpanded}*/}
        {/*      />*/}
        {/*    </div>*/}
        {/*  </Route>*/}
        {/*</TabPane>*/}
        {/* Primary-/Secondary Share Keys: a Secondary-Share-Key vendor has no
            access to questions/messages (401 server-side) - only render
            this tab for a Primary-Share-Key. */}
        {isPrimaryShareKey && (
          <TabPane
            tab={intl.formatMessage({ id: 'tab-questions' })}
            key="questions">
            <Route exact path={`${basePath}/questions`}>
              {shareKey && (
                <QuestionsPageLayoutBlock
                  shareId={tenderId}
                  shareKey={shareKey}
                  onAddQuestion={() => setVisibleCreateQuestionModal(true)}
                />
              )}
            </Route>
            <Route exact path={`${basePath}/questions/add`}>
              <CreateTenderQuestionForm
                onCreateSuccess={() =>
                  history.push(`/my-tenders/${shareIdKey}/questions`)
                }
                onCancel={() =>
                  history.push(`/my-tenders/${shareIdKey}/questions`)
                }
                shareId={tenderId}
                shareKey={shareKey}
              />
            </Route>
          </TabPane>
        )}

        {/* HELP ICON REMOVED TASK */}
        {/*<TabPane tab={intl.formatMessage({ id: 'tab-help' })} key="help">*/}
        {/*  <Route exact path={`${basePath}/help`}>*/}
        {/*    <div style={{ padding: 24, fontSize: 18 }}>*/}
        {/*      Help for vendors tab*/}
        {/*    </div>*/}
        {/*  </Route>*/}
        {/*</TabPane>*/}
      </Tabs>

      <div css={styles.actionsContainer}>
        {/* Primary-/Secondary Share Keys: Invite sits immediately to the
            LEFT of Reject in this same top-right actions row, only while
            the Subcontractors tab is active - not in the tab bar itself. */}
        {showSubcontractorsTab &&
          activeTab === 'subcontractors' &&
          inviteButtonNode}
        {/* {shareTenderDetailsQuery?.data?.state === DB_STATE.AVAILABLE && ( */}
        {!isShareKeyRejected && (
          <div
            css={styles.buttonDiv}
            onClick={() => setRejectModalVisible(true)}>
            <IconButton
              iconStyle="far"
              icon="ban"
              customStyle={styles.iconsHeader}
              backgroundOff
            />
            <p css={styles.actionsRightButton}>
              {intl.formatMessage({ id: 'reject' })}
            </p>
          </div>
        )}
        {/* )} */}

        {/* DISABLED BY JIRA: TV1-176 disable all print features */}
        {/*<div*/}
        {/*  css={styles.buttonDiv}*/}
        {/*  onClick={() => {*/}
        {/*    switch (activeTab) {*/}
        {/*      case 'documents':*/}
        {/*        handleDocumentsToPrint();*/}
        {/*        break;*/}
        {/*      case 'contract':*/}
        {/*        handleContractToPrint();*/}
        {/*        break;*/}
        {/*      case 'movements':*/}
        {/*        handleMovementsToPrint();*/}
        {/*        break;*/}
        {/*      default:*/}
        {/*        setIsPrintModalVisible(true);*/}
        {/*        break;*/}
        {/*    }*/}
        {/*  }}>*/}
        {/*  <IconButton*/}
        {/*    iconStyle="far"*/}
        {/*    icon="print"*/}
        {/*    customStyle={styles.iconsHeader}*/}
        {/*    backgroundOff*/}
        {/*  />*/}
        {/*  <p css={styles.actionsRightButton}>*/}
        {/*    {intl.formatMessage({ id: 'print' })}*/}
        {/*  </p>*/}
        {/*</div>*/}
      </div>
      {/* Primary-/Secondary Share Keys: hide the floating Questions/messages
          trigger for a Secondary-Share-Key too - same 401 rule as the
          Questions tab above. */}
      {!isOnMovements && isPrimaryShareKey && (
        <div css={styles.questionsButtonContainer}>
          <Popover
            overlayClassName="questions-mini-popover"
            overlayStyle={{
              padding: '0',
            }}
            align={{
              points: ['br', 'br'],
              offset: [0, 0],
            }}
            content={
              <QuestionsBlock
                onClose={() => setIsCommentPopoverVisible(false)}
                onAddQuestion={() => setVisibleCreateQuestionModal(true)}
                shareId={tenderId}
                shareKey={shareKey}
                tenderDetails={shareTenderDetailsQuery.data}
              />
            }
            placement="topLeft"
            trigger="click"
            visible={isCommentPopoverVisible}
            onVisibleChange={setIsCommentPopoverVisible}>
            <Button
              css={styles.messageButton}
              type="primary"
              icon={<MessageOutlined css={styles.messageIcon} rev="message" />}
            />
            {/* <IconButton icon="message-dots" /> */}
          </Popover>
        </div>
      )}

      {/* DISABLED BY JIRA: TV1-176 disable all print features */}
      {/*<Modal*/}
      {/*  visible={isPrintModalVisible}*/}
      {/*  title={intl.formatMessage({ id: 'print-preview' })}*/}
      {/*  okText={intl.formatMessage({ id: 'print' })}*/}
      {/*  onOk={() => {*/}
      {/*    // Fallback print handler for tabs that don't have a specific print handler*/}
      {/*    switch (activeTab) {*/}
      {/*      case 'details':*/}
      {/*        // If we had a details print handler, we would call it here*/}
      {/*        message.info(intl.formatMessage({ id: 'print-not-available' }));*/}
      {/*        break;*/}
      {/*      case 'help':*/}
      {/*        message.info(intl.formatMessage({ id: 'print-not-available' }));*/}
      {/*        break;*/}
      {/*      default:*/}
      {/*        message.info(intl.formatMessage({ id: 'print-not-available' }));*/}
      {/*        break;*/}
      {/*    }*/}
      {/*    setIsPrintModalVisible(false);*/}
      {/*  }}*/}
      {/*  onCancel={() => setIsPrintModalVisible(false)}*/}
      {/*  width={1000}>*/}
      {/*  <div>*/}
      {/*    <p>{intl.formatMessage({ id: 'print-not-available' })}</p>*/}
      {/*  </div>*/}
      {/*</Modal>*/}

      {/*<SimpleDeleteModal*/}
      {/*  modalTitle={`${intl.formatMessage({ id: 'share-reject' })} "${*/}
      {/*    shareTenderDetailsQuery?.data?.title*/}
      {/*  }"`}*/}
      {/*  modalIcon={['far', 'ban']}*/}
      {/*  okText={intl.formatMessage({ id: 'reject' })}*/}
      {/*  cancelText={intl.formatMessage({ id: 'cancel' })}*/}
      {/*  onOk={handleRejectShare}*/}
      {/*  isLoading={isUpdateTenderStateLoading}*/}
      {/*  onCancel={() => setIsRejectModalVisible(false)}*/}
      {/*  visible={isRejectModalVisible}*/}
      {/*/>*/}

      <CreateQuestionModal
        visible={visibleCreateQuestionModal}
        onCancel={() => setVisibleCreateQuestionModal(false)}
        shareId={tenderId}
        shareKey={shareKey}
      />

      <RejectShareKeyModal
        visible={rejectModalVisible}
        onCancel={() => setRejectModalVisible(false)}
        isSubmitting={isUpdateTenderStateLoading}
        hideHardReject
        onReject={(msg) => handleRejectKey(msg, false)}
        onRejectAndDelete={(msg) => handleRejectKey(msg, true)}
      />
    </div>
  );
};

// Subcontractors tab: extracted into its own component (mirroring
// ProjectTenderDetailsContent in TenderTabs.tsx) so it's mounted/unmounted
// by the Route itself - useDynamicHeightAndLockScroll's global scroll lock
// must only be active while this specific tab is visible, not for
// MyTendersTabs's whole lifetime (which spans every tab).
type SubcontractorsTabContentProps = Pick<
  FileTableProps,
  'selectedKeys' | 'onSelect' | 'onSelectAll' | 'setSelectedItems'
> & {
  tenderId?: string;
  shareKey?: string;
  tenderRegData?: any;
  isInviteModalOpen?: boolean;
  onCloseInviteModal: () => void;
  // Passed through from RightDisplayHome.tsx (via MyTendersTabs's own
  // ...tableProps) - tracks the CherryPickPanel/PropertiesPanel's real
  // rendered height so this tab's own dynamicHeight can be shrunk by the
  // same amount, keeping the last rows from being hidden behind it.
  bottomViewHeight?: number;
};

const SubcontractorsTabContent: React.FC<SubcontractorsTabContentProps> = ({
  tenderId,
  shareKey,
  tenderRegData,
  isInviteModalOpen,
  onCloseInviteModal,
  selectedKeys,
  onSelect,
  onSelectAll,
  setSelectedItems,
  bottomViewHeight,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const {
    containerRef,
    dynamicHeight,
  } = useDynamicHeightAndLockScroll<HTMLDivElement>();

  return (
    <div
      ref={containerRef}
      style={{
        height: bottomViewHeight
          ? `calc(${dynamicHeight} - ${bottomViewHeight}px)`
          : dynamicHeight,
      }}
      css={styles.subcontractorsPageWrapper}>
      <div css={styles.subcontractorsHeader}>
        <Typography css={styles.subcontractorsHeaderTitle}>
          {intl.formatMessage({ id: 'subcontractors-header-title' })}
        </Typography>
        <Typography css={styles.subcontractorsHeaderContent}>
          {intl.formatMessage({ id: 'subcontractors-header-content-1' })}
        </Typography>
        <Typography css={styles.subcontractorsHeaderContent}>
          {intl.formatMessage({ id: 'subcontractors-header-content-2' })}
        </Typography>
      </div>
      <div css={styles.subcontractorsTableWrapper}>
        <TenderSharesBlock
          shareId={tenderId}
          shareKey={shareKey}
          tenderRegData={tenderRegData}
          isInviteModalOpen={isInviteModalOpen}
          onCloseInviteModal={onCloseInviteModal}
          selectedKeys={selectedKeys}
          onSelect={onSelect}
          onSelectAll={onSelectAll}
          setSelectedItems={setSelectedItems}
        />
      </div>
    </div>
  );
};
